import { useState, useEffect, useRef } from "react";
import { getSupabaseFunctionUrl, supabaseFunctionHeaders } from "@/lib/config";

interface UptimeStatus {
  status: "operational" | "degraded" | "down" | "loading";
  label: string;
}

export function useUptimeStatus(): UptimeStatus {
  const [status, setStatus] = useState<UptimeStatus>({ status: "loading", label: "Checking status..." });
  const mounted = useRef(true);

  useEffect(() => {
    const fetchStatus = async () => {
      // Skip if component is unmounted or window is not defined (test environment)
      if (!mounted.current || typeof window === "undefined") {
        return;
      }

      try {
        const url = getSupabaseFunctionUrl("uptime-status");
        const res = await fetch(url, {
          headers: {
            ...supabaseFunctionHeaders(),
          },
        });

        // Only update state if component is still mounted
        if (!mounted.current) return;

        if (!res.ok) {
          setStatus({ status: "operational", label: "All systems operational" });
          return;
        }

        const data = await res.json();
        setStatus({ status: data.status, label: data.label });
      } catch {
        // Only update state if component is still mounted
        if (mounted.current) {
          setStatus({ status: "operational", label: "All systems operational" });
        }
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    
    return () => {
      mounted.current = false;
      clearInterval(interval);
    };
  }, []);

  return status;
}
